// "Set a new password" form. Shown to a user who clicked the recovery
// link in their email — at that point Supabase has already exchanged
// the recovery token for a session (detectSessionInUrl: true) and
// fired the PASSWORD_RECOVERY auth event, which AuthContext flips into
// `recoveryMode = true`. The render gate in CommissionTracker watches
// for that and renders this screen instead of the dashboard.
//
// On success → AuthContext clears recoveryMode and the app falls
// through to its normal authenticated render path.

import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import AuthLayout from './AuthLayout';

const MIN_PASSWORD_LENGTH = 8;

export default function SetNewPasswordScreen() {
  const { user, changePassword, exitRecoveryMode, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  // Detect the expired-link / invalid-token case. Supabase puts the
  // failure in the URL hash on a bad recovery click. If we land here
  // with that signal, surface a friendly message instead of an
  // unrelated form they can't submit. Read once on mount.
  const [linkError, setLinkError] = React.useState(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash || '';
    if (!hash) return null;
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const errCode = params.get('error_code');
    const errDesc = params.get('error_description');
    if (errCode === 'otp_expired') return 'This recovery link has expired. Request a new one from the sign-in page.';
    if (errCode) return errDesc || 'This recovery link is no longer valid. Request a new one.';
    return null;
  });

  const validate = () => {
    if (!password || password.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (password !== confirm) {
      return 'The two passwords don\'t match.';
    }
    return '';
  };

  const submit = async () => {
    if (pending) return;
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setPending(true);
    const result = await changePassword(password);
    setPending(false);
    if (!result.ok) {
      setError(result.error || 'Could not set the new password.');
      return;
    }
    // Show the success state for ~1.2s, then release the recovery
    // gate. Without the delay, recoveryMode flips false synchronously
    // and the parent unmounts us before the user sees "✓ Password
    // updated" — which makes the whole flow feel jarring.
    setDone(true);
    setTimeout(() => { exitRecoveryMode(); }, 1200);
  };

  const onKey = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <AuthLayout
      eyebrow="Almost done"
      heading="Set a new password"
      subheading={user?.email ? <>Signed in as <strong className="text-slate-900">{user.email}</strong></> : 'Choose a new password for your account.'}
      footer={
        <button
          onClick={signOut}
          className="text-slate-500 hover:text-slate-700 hover:underline"
        >
          Sign out and start over
        </button>
      }
    >
      {linkError ? (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-4 text-sm">
            <div className="font-semibold flex items-center gap-2">
              <span aria-hidden="true">⚠</span>
              <span>Link is no longer valid</span>
            </div>
            <div className="text-amber-800/80 mt-1">{linkError}</div>
          </div>
          <button
            onClick={signOut}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-3 rounded-lg shadow-sm transition"
          >
            Back to sign in
          </button>
        </div>
      ) : done ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-4 text-sm">
          <div className="font-semibold">✓ Password updated</div>
          <div className="text-amber-800/80 mt-1">Taking you to your CRM…</div>
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              onKeyDown={onKey}
              placeholder="At least 8 characters"
              autoFocus
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => { setConfirm(e.target.value); setError(''); }}
              onKeyDown={onKey}
              placeholder="Type it again"
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
            />
          </label>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm flex items-start gap-2">
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </div>
          )}
          <button
            onClick={submit}
            disabled={!password || !confirm || pending}
            className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-bold py-3 rounded-lg shadow-sm transition"
          >
            {pending ? 'Updating…' : 'Save new password'}
          </button>
        </div>
      )}
    </AuthLayout>
  );
}
